import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useMemo } from 'react'

import type { NoSpenderListProjection } from '@api'
import type { ColumnDef, PaginationState } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'

import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs'
import { useNoSpenderListMutations } from '@/modules/creator-settings/lists/no-spenders/hooks/useGetNoSpenderListMutations'
import type { NoSpendersListFormValues } from '@/modules/creator-settings/lists/no-spenders/types'

import { NoSpendersActionButton } from './no-spenders-action-button'

const listToFormValues = (
  list: NoSpenderListProjection,
): NoSpendersListFormValues => {
  return {
    name: list.name ?? '',
    minimumDays: list.days ?? 0,
  }
}

interface NoSpendersTableProps {
  paginationState: PaginationState
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>
  data: NoSpenderListProjection[]
  isFetching: boolean
  pageCount: number
}

export const NoSpendersTable = ({
  paginationState,
  onPaginationChange,
  data,
  isFetching,
  pageCount,
}: NoSpendersTableProps) => {
  const creatorId = useUnit($creatorId)

  const { updateNoSpenderList, deleteNoSpenderList } =
    useNoSpenderListMutations(creatorId ?? 0)

  const handleDelete = useCallback(
    (list: NoSpenderListProjection) => {
      openDialog({
        type: DIALOG_TYPE.CONFIRMATION,
        title: 'Delete Spenders Lists',
        desc: 'Delete the spenders list you selected will delete it permanently.',
        confirmText: 'Delete',
        confirmButtonVariant: 'destructive',
        confirmButtonTestId: 'delete-list-confirm-button',
        onConfirm: () => deleteNoSpenderList.mutateAsync(list.id!),
      })
    },
    [deleteNoSpenderList],
  )

  const handleSave = useCallback(
    (id: number) =>
      async (
        values: NoSpendersListFormValues,
      ): Promise<unknown | undefined> => {
        return updateNoSpenderList.mutateAsync({
          id,
          name: values.name,
          days: values.minimumDays,
        })
      },
    [updateNoSpenderList],
  )

  const columns: ColumnDef<NoSpenderListProjection>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'List Name',
        width: 200,
      },
      {
        accessorKey: 'fans',
        header: 'Number of Fans',
        cell: ({ row }) => {
          return <span>{row.original.fansSize ?? 0}</span>
        },
      },
      {
        accessorKey: 'updatedAt',
        header: 'Date Refreshed',
        cell: ({ row }) => {
          return (
            <span>
              {row.original.lastProcessedAt
                ? format(
                    new Date(
                      row.original.lastProcessedAt ??
                        row.original.lastProcessedAt ??
                        '',
                    ),
                    'LLL dd, y hh:mm a',
                  )
                : '-'}
            </span>
          )
        },
        meta: {
          skeleton: {
            customCell: () => {
              return <Skeleton className="h-4 w-40 dark:bg-slate-500" />
            },
          },
        },
        sortingFn: (a, b) => {
          const aDate =
            a.original.lastProcessedAt ?? a.original.lastProcessedAt ?? ''
          const bDate =
            b.original.lastProcessedAt ?? b.original.lastProcessedAt ?? ''

          return new Date(bDate).getTime() - new Date(aDate).getTime()
        },
      },
      {
        accessorKey: 'action',
        header: () => <span className="flex w-full justify-end">Actions</span>,
        meta: {
          isActionColumn: true,
        },
        cell: ({ row }) => {
          return (
            <NoSpendersActionButton
              onDelete={handleDelete}
              onEdit={(list) =>
                openDialog({
                  type: DIALOG_TYPE.NO_SPENDER_LIST_FORM,
                  onSubmit: handleSave(list.id!),
                  title: 'Edit No Spenders List',
                  submitLabel: 'Save',
                  initialValues: listToFormValues(list),
                })
              }
              onViewFans={(list) =>
                openDialog({
                  type: DIALOG_TYPE.NO_SPENDER_LIST_VIEW_FANS,
                  listId: list.id!,
                  creatorId: creatorId ?? 0,
                })
              }
              row={row}
            />
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creatorId, handleDelete, handleSave, openDialog],
  )

  return (
    <DataTable
      enablePagination
      columns={columns}
      data={data ?? []}
      isLoading={isFetching}
      paginationState={paginationState}
      onPaginationChange={onPaginationChange}
      tbodyMinHeight={false}
      pageCount={pageCount}
      enableSorting
      testId="no-spender-list-table"
    />
  )
}
